// Utilities for managing favorite stations in localStorage

const FAVORITES_STORAGE_KEY = 'bamako-fuel-favorites';

export interface FavoriteStation {
  id: number;
  name: string;
  brand: string;
  address: string;
  commune: string;
  addedAt: string;
}

// Get all favorite stations from localStorage
export const getFavorites = (): FavoriteStation[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.warn('Error reading favorites from localStorage:', error);
    return [];
  }
};

// Add a station to favorites
export const addToFavorites = (station: {
  id: number;
  name: string;
  brand: string;
  address: string;
  commune: string;
}): void => {
  try {
    const favorites = getFavorites();

    // Check if already in favorites
    if (favorites.some(fav => fav.id === station.id)) {
      return; // Already in favorites
    }

    const newFavorite: FavoriteStation = {
      ...station,
      addedAt: new Date().toISOString()
    };

    const updatedFavorites = [...favorites, newFavorite];
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updatedFavorites));
  } catch (error) {
    console.warn('Error adding to favorites:', error);
  }
};

// Remove a station from favorites
export const removeFromFavorites = (stationId: number): void => {
  try {
    const favorites = getFavorites();
    const updatedFavorites = favorites.filter(fav => fav.id !== stationId);
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updatedFavorites));
  } catch (error) {
    console.warn('Error removing from favorites:', error);
  }
};

// Check if a station is in favorites
export const isFavorite = (stationId: number): boolean => {
  const favorites = getFavorites();
  return favorites.some(fav => fav.id === stationId);
};

// Get favorite station IDs only
export const getFavoriteIds = (): number[] => {
  const favorites = getFavorites();
  return favorites.map(fav => fav.id);
};

// Clear all favorites
export const clearAllFavorites = (): void => {
  try {
    localStorage.removeItem(FAVORITES_STORAGE_KEY);
  } catch (error) {
    console.warn('Error clearing favorites:', error);
  }
};